import type React from 'react';
import { useEffect, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import type { BillingFrequency, FrequencyTotals } from '@proposify/pricing-engine';
import { Heading } from 'components/typography';
import { isAccessTokenSet } from 'utils/authentication';
import { useMediaQuery } from 'hooks/useMediaQuery';

import './styles.less';
import { PreviewActionMenu } from '../ActiveMenu';
import { ApiUserPermissionsRepository } from 'services/permissions/implementations/ApiUserPermissionsRepository';
import { UserPermission } from '../../../services/permissions/entities/UserPermissions';
import { useQuery } from 'react-query';
import { QueryKeys } from 'utils/queryKeys';
import CommentsButton from 'components/commentsbutton';
import { formatCurrencyValue } from '../../editor/helpers/formatCurrencyValue.ts';
import { getHeaderFrequencySummary } from './headerFrequencySummary';
import { PRICE_MAX_VALUE } from 'components/editor/hooks/useFieldValidator';
import { previewMenuTestIds } from './PreviewMenu.test-ids';
import { InsightsButton } from 'components/Insights/InsightsButton.tsx';

interface PreviewMenuProps {
  documentId: string;
  contentId: string;
  documentTitle: string;
  documentProspectName: string;
  documentPrice: number;
  isDocumentBelongsToSameAccount: boolean;
  displayDocumentTotal?: boolean;
  currencySettings?: {
    currencyCode: string;
    currencySign: string;
  };
  /** Engine decimal strings per frequency; null suppresses the per-frequency strip. */
  frequencyTotals?: FrequencyTotals | null;
  presentFrequencies?: BillingFrequency[];
}

const PreviewMenu: React.FC<PreviewMenuProps> = ({
  documentId,
  contentId,
  documentTitle,
  documentProspectName,
  documentPrice,
  isDocumentBelongsToSameAccount,
  displayDocumentTotal = true,
  currencySettings,
  frequencyTotals = null,
  presentFrequencies = [],
}) => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const frequencyEntries = getHeaderFrequencySummary(presentFrequencies, frequencyTotals);
  const hasExceededMaxTotal = documentPrice > PRICE_MAX_VALUE;

  const formatHeaderValue = (value: number): string =>
    currencySettings?.currencySign
      ? formatCurrencyValue(value, 'en-US', currencySettings?.currencySign)
      : value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const { data: userPermissions } = useQuery<UserPermission[]>({
    queryKey: QueryKeys.currentUserPermissions,
    queryFn: () => new ApiUserPermissionsRepository().getUserPermissions(),
    enabled: isAccessTokenSet(),
  });

  const handleDocumentTitleClick = () => {
    setIsTooltipVisible(true);
  };

  const hideTooltipOnScroll = () => {
    setIsTooltipVisible(false);
  };

  const hideTooltipOnClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.preview__header')) {
      setIsTooltipVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener('wheel', hideTooltipOnScroll);
    document.addEventListener('touchmove', hideTooltipOnScroll);
    document.addEventListener('click', hideTooltipOnClick);

    return () => {
      document.removeEventListener('wheel', hideTooltipOnScroll);
      document.removeEventListener('touchmove', hideTooltipOnScroll);
      document.removeEventListener('click', hideTooltipOnClick);
    };
  }, []);
  const canShareDocument = userPermissions && !userPermissions.includes(UserPermission.isCollaborator);
  const canViewActionMenu = isAccessTokenSet() && isDocumentBelongsToSameAccount && canShareDocument;
  const hasCompanyName = Boolean(documentProspectName?.trim());
  const primaryHeadingText = hasCompanyName ? documentProspectName : documentTitle;

  return (
    <div className="preview__header__content">
      <div className="preview_header__column--left">
        <Tooltip placement="bottom" title={primaryHeadingText} open={isMobile ? isTooltipVisible : false}>
          <div onClick={handleDocumentTitleClick}>
            <Heading level={3} data-testid={previewMenuTestIds.previewTitle}>
              {primaryHeadingText}
            </Heading>
          </div>
        </Tooltip>
        <div className="preview_header__subheading">
          {hasCompanyName && (
            <span className="preview_header__document_name" data-testid={previewMenuTestIds.headerDocumentName}>
              {documentTitle}
            </span>
          )}
          {displayDocumentTotal &&
            (frequencyEntries.length > 0 ? (
              <div
                className="preview_header__subheading_block preview_header__frequency_summary"
                data-testid={previewMenuTestIds.headerFrequencySummary}
              >
                {frequencyEntries.map((entry) => (
                  <span
                    key={entry.frequency}
                    className="preview_header__frequency_entry"
                    data-testid={previewMenuTestIds.headerFrequency(entry.frequency)}
                  >
                    <span style={{ color: 'rgba(65, 72, 77, 1)' }}>{formatHeaderValue(Number(entry.total))}</span>
                    {entry.frequency === 'one-time' ? ' ' : ''}
                    {t(entry.suffixKey, { defaultValue: entry.fallbackSuffix })}
                  </span>
                ))}
              </div>
            ) : hasExceededMaxTotal ? (
              <Tooltip placement="bottom" title={t('editor.error_messages.over_max_value', { maxValue: PRICE_MAX_VALUE })}>
                <div className="preview_header__subheading_block preview_header__subheading_block--error">
                  {formatHeaderValue(documentPrice)}
                </div>
              </Tooltip>
            ) : (
              <div className="preview_header__subheading_block">{formatHeaderValue(documentPrice)}</div>
            ))}
        </div>
      </div>
      <div className="preview_header__column--right">
        {canViewActionMenu && <InsightsButton documentId={documentId} />}
        <CommentsButton />
        <PreviewActionMenu
          contentId={contentId}
          documentId={documentId}
          documentProspectName={documentProspectName}
          documentTitle={documentTitle}
          canViewShareButton={canViewActionMenu}
        />
      </div>
    </div>
  );
};

export default PreviewMenu;
